import { useEffect, useState } from "react";
import { Failure, GhostButton, Loading, Note, PrimaryButton, Screen } from "../components/Entry";
import { getFlowError, logoutUrl, whoami, type Session } from "../lib/kratos";
import { go } from "../lib/flow";
import { navigate } from "../lib/router";

/** Where Kratos sends a browser with nothing else to do: who is signed in, and the way out. */
export function Home() {
  const [session, setSession] = useState<Session | null | undefined>(undefined);
  useEffect(() => {
    let alive = true;
    whoami()
      .then(s => alive && (s ? setSession(s) : navigate("/login", { replace: true })))
      .catch(() => alive && setSession(null));
    return () => {
      alive = false;
    };
  }, []);
  if (session === undefined) return <Loading />;
  if (session === null) {
    return (
      <Screen title="Kora account">
        <Failure text="The account service can’t be reached. Check your connection and try again." />
      </Screen>
    );
  }
  const signOut = async () => go((await logoutUrl(window.location.origin + "/login")) ?? "/login");
  return (
    <Screen title="You’re signed in">
      <form
        noValidate
        className="entry-form"
        onSubmit={event => {
          event.preventDefault();
          void signOut();
        }}
      >
        <Note>Signed in as {session.identity.traits.email}</Note>
        <PrimaryButton>Sign out</PrimaryButton>
        <GhostButton onClick={() => navigate("/settings")}>Account settings</GhostButton>
      </form>
    </Screen>
  );
}

export function ErrorPage({ query }: { query: URLSearchParams }) {
  const [text, setText] = useState<string>();
  const id = query.get("id");
  useEffect(() => {
    // Hydra reports OAuth errors in the query; Kratos stores its own and hands over an id.
    const oauth = query.get("error_description") || query.get("error");
    if (oauth) return setText(oauth);
    if (!id) return setText("Something went wrong.");
    getFlowError(id)
      .then(error => setText(error?.reason || error?.message || "Something went wrong."))
      .catch(() => setText("Something went wrong."));
  }, [id, query]);
  return (
    <Screen title="Something went wrong">
      <div className="entry-form">
        {text ? <Failure text={text} /> : null}
        <GhostButton onClick={() => navigate("/login")}>Back to sign in</GhostButton>
      </div>
    </Screen>
  );
}

export function NotFound() {
  return (
    <Screen title="Page not found">
      <div className="entry-form">
        <Note>This page doesn’t exist.</Note>
        <GhostButton onClick={() => navigate("/login")}>Back to sign in</GhostButton>
      </div>
    </Screen>
  );
}
